import { Link } from "react-router-dom";
import Marquee from "@/components/Marquee";
import ServiceCard, { SERVICE_TIERS } from "@/components/ServiceCard";
import ContactForm from "@/components/ContactForm";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1708793699492-5fa208f52dcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxob21lJTIwZGVzayUyMHdpcmVsZXNzJTIwcHJpbnRlcnxlbnwwfHx8fDE3OTE5OTU2NDV8MA&ixlib=rb-4.1.0&q=85";
const BAND_IMAGE =
  "https://images.unsplash.com/photo-1671040690726-b78261eff126?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHx0ZWNobmljaWFuJTIwdmFuJTIwcGFydHMlMjB0b29sc3xlbnwwfHx8fDE3OTE5OTU2NDV8MA&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="py-16 px-6 max-w-6xl mx-auto border-b border-poster-border">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="px-2 py-0.5 bg-poster-accent text-white text-[11px] font-mono-lbl uppercase tracking-wider font-bold">
            Same-Day Priority
          </span>
          <span className="text-xs font-mono-lbl text-poster-muted uppercase tracking-wider">
            Independent support for Canon brand printers
          </span>
        </div>

        <h1 className="font-poster text-[clamp(48px,7.5vw,104px)] uppercase tracking-tight text-poster-ink leading-[0.92] my-4">
          <span className="ln"><span>Fast same-day printer</span></span>
          <span className="ln"><span>solutions for homes</span></span>
          <span className="ln"><span className="text-poster-accent">and office teams.</span></span>
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center mt-8">
          <div className="md:col-span-7">
            <p className="text-base text-slate-600 leading-relaxed max-w-lg">
              From paper jam lockouts and clogged printhead nozzles to complex office network printing, get direct
              local on-site visits and fast remote sessions today.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-book-help-button"
                className="px-7 py-3.5 bg-poster-accent hover:bg-sky-700 text-white font-mono-lbl text-xs uppercase tracking-widest font-bold shadow-md transition-colors"
              >
                Book Immediate Help &rarr;
              </Link>
              <Link
                to="/services"
                data-testid="hero-view-services-button"
                className="px-7 py-3.5 bg-white border border-poster-border text-poster-ink hover:bg-slate-100 font-mono-lbl text-xs uppercase tracking-widest font-bold transition-colors"
              >
                View Service Matrix
              </Link>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="d3-wipe relative h-64 border-2 border-poster-ink shadow-[6px_6px_0px_#0F172A] overflow-hidden d3-drift">
              <img
                src={HERO_IMAGE}
                alt="Technician running a nozzle test sheet on a desktop printer"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent p-5 flex flex-col justify-end text-white">
                <span className="text-[10px] font-mono-lbl text-sky-300 uppercase tracking-widest">
                  Active Dispatch Target
                </span>
                <p className="font-poster text-2xl uppercase tracking-wide">Same-Day Local Ticket</p>
                <span className="text-xs text-white/80">Average on-site dispatch under 2 hours</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* SERVICES TEASER */}
      <section className="py-20 px-6 max-w-6xl mx-auto border-b border-poster-border">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
          <div>
            <span className="text-xs font-mono-lbl uppercase tracking-widest text-poster-accent font-bold">
              Solutions
            </span>
            <h2 className="font-poster text-4xl uppercase text-poster-ink mt-1">Independent Support Tiers</h2>
          </div>
          <p className="text-xs font-mono-lbl uppercase text-poster-muted mt-2 md:mt-0">
            Clear Pricing &bull; Fast Action
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICE_TIERS.map((tier) => (
            <ServiceCard key={tier.id} tier={tier} />
          ))}
        </div>
      </section>

      {/* DISPATCH / COVERAGE */}
      <section className="bg-poster-ink text-white py-20 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 h-72 border-2 border-white/20 relative overflow-hidden d3-drift">
            <img
              src={BAND_IMAGE}
              alt="Support technician preparing parts for a fast local arrival"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent p-6 flex flex-col justify-end">
              <p className="font-poster text-2xl uppercase text-white">Local + Remote Grid</p>
              <span className="text-xs text-sky-300 font-mono-lbl">
                Direct support coverage for home &amp; business
              </span>
            </div>
          </div>

          <div className="lg:col-span-7">
            <span className="text-xs font-mono-lbl uppercase tracking-widest text-poster-cyan font-bold">
              Fast Reach
            </span>
            <h2 className="font-poster text-4xl uppercase text-white mt-1">
              Local On-Site &amp; Nationwide Remote
            </h2>
            <p className="text-slate-300 text-sm mt-4 leading-relaxed">
              Whether you require hands-on hardware repairs at your home or workplace, or immediate software
              troubleshooting via secure remote login, we are equipped to assist same-day.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-4 border-t border-white/10 pt-6">
              <div>
                <span className="font-poster text-4xl text-white">
                  <span className="odo"><span className="reel" style={{ "--d": 1 }}>0<br />1<br />2<br />3</span></span>00%
                </span>
                <span className="text-[10px] font-mono-lbl uppercase text-slate-400 block mt-1">Independent</span>
              </div>
              <div>
                <span className="font-poster text-4xl text-white">
                  &lt;<span className="odo"><span className="reel" style={{ "--d": 2 }}>0<br />1<br />2<br />3</span></span>h
                </span>
                <span className="text-[10px] font-mono-lbl uppercase text-slate-400 block mt-1">Dispatch Target</span>
              </div>
              <div>
                <span className="font-poster text-4xl text-poster-cyan">Same-Day</span>
                <span className="text-[10px] font-mono-lbl uppercase text-slate-400 block mt-1">Commitment</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HONEST REVIEWS PLACEHOLDER */}
      <section className="py-16 px-6 max-w-4xl mx-auto text-center border-b border-poster-border">
        <span className="text-xs font-mono-lbl uppercase tracking-widest text-poster-muted">Verified Feed</span>
        <h2 className="font-poster text-3xl uppercase text-poster-ink mt-1">Client Service Log</h2>
        <div
          data-testid="reviews-placeholder"
          className="mt-6 p-6 border-2 border-dashed border-poster-border bg-slate-200"
        >
          <p className="text-xs text-slate-600 italic font-mono-lbl">
            "Verified user reviews and repair histories will be presented here as current tickets finalize."
          </p>
          <span className="inline-block mt-2 text-[10px] font-mono-lbl uppercase text-poster-muted">
            Independent Field Records
          </span>
        </div>
      </section>

      {/* FORM */}
      <section className="py-20 px-6 max-w-4xl mx-auto">
        <div className="bg-white border-2 border-poster-ink shadow-[8px_8px_0px_#0F172A] p-8 md:p-12">
          <div className="text-center max-w-md mx-auto mb-8">
            <span className="text-xs font-mono-lbl uppercase tracking-widest text-poster-accent font-bold">
              Fast Action Form
            </span>
            <h2 className="font-poster text-4xl uppercase text-poster-ink mt-1">Request Same-Day Fix</h2>
            <p className="text-xs text-poster-muted mt-1">Connect with an independent technician in minutes.</p>
          </div>

          <div className="mb-6 p-3 bg-sky-50 border border-sky-200 flex items-center gap-3">
            <div className="w-8 h-8 bg-poster-accent text-white flex items-center justify-center font-poster text-base shrink-0">
              !
            </div>
            <p className="text-xs text-sky-800 font-medium">
              Clear diagnostics with no hidden charges. Upfront quotes before repairs proceed.
            </p>
          </div>

          <ContactForm />
        </div>
      </section>
    </main>
  );
}
